/** Мелкие элементы портала поверх каркаса: цветная точка, пометка статуса, отметка времени. */

import type { ReactNode } from 'react';
import { formatShortDateTime, getRestaurantTimeZone } from '@shell/format';

/** Цвет из ответа сервера → класс. Незнакомое значение — серая точка, а не ошибка. */
export function tone(colour: string | null | undefined): 'red' | 'yellow' | 'green' | 'grey' {
  switch (colour) {
    case 'red':
      return 'red';
    case 'yellow':
    case 'waiting_draft':
      return 'yellow';
    case 'green':
      return 'green';
    default:
      return 'grey';
  }
}

export function Dot({ colour }: { colour: string | null | undefined }) {
  return <span className={`dot dot--${tone(colour)}`} aria-hidden="true" />;
}

export function Pill({ colour, children }: { colour?: string | null; children: ReactNode }) {
  return <span className={`pill pill--${tone(colour)}`}>{children}</span>;
}

/** «14:02» сегодня, «15.09 14:02» — раньше. */
export function when(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tz = getRestaurantTimeZone();
  const day = (d: Date) => new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeZone: tz }).format(d);
  if (day(date) === day(new Date())) {
    return new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    }).format(date);
  }
  return formatShortDateTime(date);
}

/** Дата из ISO без времени: «12.09.2026». */
export function isoDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

/** Ответ сервера после действия — одна строка над кнопками. */
export function Notice({
  kind = 'ok',
  children,
}: {
  kind?: 'ok' | 'error';
  children: ReactNode;
}) {
  return (
    <p className={`notice notice--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}
